import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { countAvailableConnectors, isStationOffline } from '@/lib/stationStats';
import type { StationDetailOut } from '@/api/stations';

interface Props {
  station: StationDetailOut;
  selected?: boolean;
  /** Estação mais próxima do usuário (por geolocalização) — ganha um anel na
   * cor-assinatura da marca pra se destacar das demais sem confundir com o
   * significado de cor da bolinha de status (livre/carregando/ocupado). */
  isNearest?: boolean;
  onClick?: () => void;
}

const NEAREST_RING_COLOR = '#C04CAE';

type Tone = 'free' | 'charging' | 'busy' | 'offline';

function toneOf(station: StationDetailOut): Tone {
  if (isStationOffline(station)) return 'offline';
  const { available } = countAvailableConnectors(station);
  if (available > 0) return 'free';
  if (station.chargers.some((c) => c.state === 'CHARGING')) return 'charging';
  return 'busy';
}

const DOT_CLASS: Record<Tone, string> = {
  free: 'bg-bow-green',
  charging: 'bg-bow-blue',
  busy: 'bg-bow-royal/40',
  offline: 'bg-[#9AA0B4]'
};

/** Pino da Bow no mapa: círculo branco + marca colorida + bolinha de status
 * (verde=livre, azul=carregando, cinza=ocupado, cinza com traço=fora do ar,
 * marca esmaecida) — porte de `mapfirst-core.jsx` · `BowPin` (ref. Saulo
 * 2026-07-05; estado offline do artboard "Home · peek · fora do ar"). */
export function MapPin({ station, selected = false, isNearest = false, onClick }: Props) {
  const lat = Number(station.latitude);
  const lng = Number(station.longitude);
  if (station.latitude === null || station.longitude === null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  const tone = toneOf(station);
  const offline = tone === 'offline';
  const size = selected ? 56 : 46;
  const pulse = tone === 'charging';

  return (
    <AdvancedMarker position={{ lat, lng }} onClick={onClick} zIndex={selected ? 4 : 2}>
      <div className="flex flex-col items-center">
        <div
          className="relative flex items-center justify-center rounded-full border-[2.5px] border-white bg-white"
          style={{
            width: size,
            height: size,
            boxShadow: [
              isNearest ? `0 0 0 3px ${NEAREST_RING_COLOR}` : null,
              selected ? '0 10px 24px rgba(0,27,116,0.4)' : '0 5px 14px rgba(0,27,116,0.28)'
            ]
              .filter(Boolean)
              .join(', ')
          }}
        >
          <img
            src="/logo/logo-bow-13.png"
            alt=""
            aria-hidden
            style={{
              width: size * 0.66,
              height: size * 0.66,
              objectFit: 'contain',
              ...(offline ? { filter: 'grayscale(1)', opacity: 0.4 } : {})
            }}
          />
          <span
            className={`absolute -right-0.5 -top-0.5 flex items-center justify-center rounded-full border-[2.5px] border-white ${DOT_CLASS[tone]} ${
              pulse ? 'animate-pulse' : ''
            }`}
            style={{ width: selected ? 17 : 15, height: selected ? 17 : 15 }}
          >
            {offline && (
              <svg width={selected ? 8 : 7} height={selected ? 8 : 7} viewBox="0 0 8 8" aria-hidden>
                <path d="M1.2 4h5.6" stroke="#FFFFFF" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            )}
          </span>
        </div>
        <div className="h-[7px] w-[2.5px] rounded-sm bg-[#C7C0AE]" />
        {offline && (selected || isNearest) && (
          <div
            className="mt-[5px] whitespace-nowrap rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-bow-royal/55"
            style={{ boxShadow: '0 3px 10px rgba(0,27,116,0.14)' }}
          >
            {station.name.replace(/^Bow\s+/, '')} · Fora do ar
          </div>
        )}
      </div>
    </AdvancedMarker>
  );
}
